import { RouteObject, createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { PassingView } from '@/features/desktop/routes/passing-view';
import { useMe } from '@/features/auth/api/get-me';
import { useSettingsStore } from '@/stores/settingsStore';
import storage from '@/utils/storage';
import { LoginForm } from '@/features/auth/components/login-form';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { PassagePageSkeleton } from '@/features/desktop/components/passage-page-skeleton';
import useEmbededAuth from '@/hooks/use-embeded-auth';
import { Statistics } from '@/features/mobile/routes/statistics';
import { Settings } from '@/features/mobile/routes/settings';
import { AttendeeList } from '@/features/mobile/routes/attendee-list';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { MobileLayout } from '@/features/mobile/components/mobile-layout';
import { Scan } from '@/features/mobile/components/scan';
import { AttendeeDetails } from '@/features/mobile/routes/attendee-details';
import { SignalRContext } from '@/providers/SignalRProvider';

export const protectedRoutes: RouteObject[] = [
  {
    path: '/',
    element: <MobileRedirect />,
  },
  { path: '/passing-view', element: <PassingView /> },
  {
    path: '/checkin',
    element: <MobileLayout />,
    children: [
      {
        index: true,
        element: <Scan />,
      },
      {
        path: 'scan',
        element: <Scan />,
      },
      {
        path: 'statistics',
        element: <Statistics />,
      },
      {
        path: 'settings',
        element: <Settings />,
      },
      {
        path: 'attendee-list',
        element: <AttendeeList />,
      },
      {
        path: 'attendee-details',
        element: <AttendeeDetails />,
      },
    ],
  },
];

const publicRoutes: RouteObject[] = [
  {
    path: '*',
    element: <LoginForm />,
  },
];

function MobileRedirect() {
  const isMobile = useIsMobile(768);
  return <Navigate to={isMobile ? '/checkin' : '/passing-view'} replace />;
}

export const AppRoutes = () => {
  const currentEventId = useSettingsStore(state => state.eventId);
  const breakoutId = useSettingsStore(state => state.breakoutId);
  const setCurrentEventId = useSettingsStore(state => state.setEventId);
  const setBreakoutId = useSettingsStore(state => state.setBreakoutId);

  //TODO handle emeded auth
  //TODO handle mobile and desktop routes
  const isOnline = useOnlineStatus();

  // The page is embedded (probably from onsite in Jirango)
  const isEmbedded = window.self !== window.top;

  const expectedEmbeddedOrigin = import.meta.env.VITE_MVC_ORIGIN || (import.meta.env.MODE === 'debug' ? 'https://localhost:44331' : 'https://jirango.com');

  if (isEmbedded && !import.meta.env.VITE_MVC_ORIGIN) {
    console.warn('[EmbeddedAuth] VITE_MVC_ORIGIN is not configured. Falling back to mode-based default origin.');
  }

  const isAutenticated = useEmbededAuth(expectedEmbeddedOrigin, isEmbedded);

  const { data: user, isFetched } = useMe({
    config: {
      refetchOnMount:false,
      enabled: isEmbedded ? isAutenticated : !!storage.getAccessToken() && isOnline,
      refetchInterval: 1000 * 60 * 5, // 5 minutes,
      onSuccess: data => {
        if (data?.defaultEventId && !currentEventId) {
          setCurrentEventId(data.defaultEventId);
          setBreakoutId(data.defaultEventId);
        }
        SignalRContext.invoke('onsiteHandshake', {
          userGuid: user?.userGuid,
          eventId: breakoutId ?? currentEventId ?? data.defaultEventId,
          ConnectionId: '123',
        });

        if (!storage.getDeviceId()) {
          storage.setDeviceId();
        }
      },
    },
  });


  const routes = isFetched && user && storage.getAccessToken() ? protectedRoutes : [...publicRoutes, { path: '*', element: <PassagePageSkeleton /> }];

  const router = createBrowserRouter([...routes]);

  return <RouterProvider router={router} />;
};
